import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, addDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiMonitor, FiCheckCircle, FiXCircle, FiSettings, FiPrinter } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Terminals() {
  const [terminals, setTerminals] = useState([]);
  const [terminalSettings, setTerminalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState('appearance');
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [stats, setStats] = useState({
    totalTerminals: 0,
    activeTerminals: 0,
    inactiveTerminals: 0
  });
  const { selectedHierarchy } = useAuth();

  // Metro colors for theme selection (matching Flutter app)
  const metroColors = [
    { name: 'Blue', color: 0xFF2196F3, hex: '#2196F3' },
    { name: 'Red', color: 0xFFF44336, hex: '#F44336' },
    { name: 'Green', color: 0xFF4CAF50, hex: '#4CAF50' },
    { name: 'Purple', color: 0xFF9C27B0, hex: '#9C27B0' },
    { name: 'Orange', color: 0xFFFF9800, hex: '#FF9800' },
    { name: 'Teal', color: 0xFF009688, hex: '#009688' },
    { name: 'Pink', color: 0xFFE91E63, hex: '#E91E63' },
    { name: 'Indigo', color: 0xFF3F51B5, hex: '#3F51B5' },
    { name: 'Lime', color: 0xFFCDDC39, hex: '#CDDC39' },
    { name: 'Cyan', color: 0xFF00BCD4, hex: '#00BCD4' },
    { name: 'Amber', color: 0xFFFFC107, hex: '#FFC107' },
    { name: 'Brown', color: 0xFF795548, hex: '#795548' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    deviceId: '',
    isActive: true
  });

  const [settingsFormData, setSettingsFormData] = useState({
    // Theme settings
    themeMode: 'light',
    showStockInfo: true,
    categoryFontSize: 14,
    productFontSize: 15,
    primaryColor: 0xFF2196F3,
    
    // Security settings
    pinAuthEnabled: false,
    pinCode: '',
    faceAuthEnabled: false,
    faceAuthFastMode: false,
    faceAuthThreshold: 0.75,
    
    // Weather settings
    weatherEnabled: false,
    weatherApiKey: '',
    useCurrentLocation: true,
    manualLatitude: '',
    manualLongitude: '',
    weatherForceRefresh: false,
    weatherApiProvider: 'openmeteo',
    weatherCacheDurationMinutes: 60,
    
    // SMS & WhatsApp settings
    smsEnabled: false,
    smsApiKey: '',
    smsPhoneNumber: '',
    whatsappEnabled: false,
    whatsappApiKey: '',
    whatsappPhoneNumberId: '',
    whatsappRecipientNumber: '',
    
    // Auto sync settings
    autoSyncEnabled: true,
    syncFrequencyMinutes: 15,
    
    // Animation settings
    autoDismissEnabled: true,
    autoDismissDuration: '3000',
    confettiEnabled: false,
    confettiSpeed: '1.0',
    
    // Printer settings
    printerEnabled: false,
    printerConnectionType: 'Network',
    printerIp: '',
    printerPort: '9100',
    printerPaperWidth: '58mm',
    printerCharacterSet: 'PC437',
    printerDpi: 203,
    printerCutPaper: true,
    printerBoldHeader: true,
    printerLogoEnabled: false,
    printerHeaderText: '',
    printerFooterText: '',
    printerAutoPrintReceipt: true,
    lastBluetoothDeviceName: '',
    lastBluetoothDeviceAddress: '',
    
    // KOT Printer settings
    kotPrinterEnabled: false,
    kotPrinterConnectionType: 'Network',
    kotPrinterIp: '',
    kotPrinterPort: '9100',
    kotPrinterPaperWidth: '58mm',
    kotPrinterCharacterSet: 'PC437',
    kotPrinterDpi: 203,
    kotPrinterCutPaper: true,
    kotPrinterBoldHeader: true,
    kotPrinterLogoEnabled: false,
    kotPrinterHeaderText: 'KITCHEN ORDER TICKET',
    kotPrinterFooterText: 'Kitchen - Prepare Order',
    kotAutoPrintKOT: false,
    lastKOTBluetoothDeviceName: '',
    lastKOTBluetoothDeviceAddress: '',
    
    // Currency settings
    currencyCode: 'INR',
    currencySymbol: '₹',
    
    // UPI settings
    upiIds: [],
    upiMerchantName: '',
    
    // Payment settings
    splitPaymentEnabled: true,
    expressCheckoutEnabled: false,
    employeeAuthValidationOnCheckoutEnabled: false,
    
    // POS Screen settings
    onDemandTabEnabled: false,
    
    // Discount settings
    discountPercentageStepFactor: 0.05,
    discountFixedAmountStepFactor: 5.0,
    discountItemsPerPage: 25
  });

  useEffect(() => {
    if (selectedHierarchy?.outlet?.id) {
      loadTerminals();
    }
  }, [selectedHierarchy]);

  const loadTerminals = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      setLoading(true);
      
      // Set up real-time listener for terminals
      const terminalsQuery = query(
        collection(db, 'terminals'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const unsubscribe = onSnapshot(terminalsQuery, async (snapshot) => {
        const terminalsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setTerminals(terminalsData);
        
        // Load settings for each terminal
        const settingsData = {};
        for (const terminal of terminalsData) {
          try {
            const settingsDoc = await getDocs(
              query(
                collection(db, 'terminal_settings'),
                where('terminalId', '==', terminal.id)
              )
            );
            
            if (!settingsDoc.empty) {
              settingsData[terminal.id] = {
                id: settingsDoc.docs[0].id,
                ...settingsDoc.docs[0].data()
              };
            }
          } catch (error) {
            console.error(`Error loading settings for terminal ${terminal.id}:`, error);
          }
        }
        
        setTerminalSettings(settingsData);
        
        // Calculate stats
        const activeCount = terminalsData.filter(t => t.isActive).length;
        setStats({
          totalTerminals: terminalsData.length,
          activeTerminals: activeCount,
          inactiveTerminals: terminalsData.length - activeCount
        });
        
        setLoading(false);
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading terminals:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedHierarchy?.outlet?.id) {
      alert('Please select an outlet first');
      return;
    }

    try {
      const terminalData = {
        ...formData,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };

      if (editingTerminal) {
        // Update existing terminal
        await updateDoc(doc(db, 'terminals', editingTerminal.id), terminalData);
        alert('Terminal updated successfully');
      } else {
        // Create new terminal
        terminalData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'terminals'), terminalData);
        
        // Create default settings for new terminal with all fields
        await addDoc(collection(db, 'terminal_settings'), {
          terminalId: docRef.id,
          outletId: selectedHierarchy.outlet.id,
          
          // Theme settings
          themeMode: 'light',
          showStockInfo: true,
          categoryFontSize: 14,
          productFontSize: 15,
          primaryColor: 0xFF2196F3,
          
          // Security settings
          pinAuthEnabled: false,
          pinCode: '',
          faceAuthEnabled: false,
          faceAuthFastMode: false,
          faceAuthThreshold: 0.75,
          
          // Weather settings
          weatherEnabled: false,
          weatherApiKey: '',
          useCurrentLocation: true,
          manualLatitude: '',
          manualLongitude: '',
          weatherForceRefresh: false,
          weatherApiProvider: 'openmeteo',
          weatherCacheDurationMinutes: 60,
          
          // SMS & WhatsApp settings
          smsEnabled: false,
          smsApiKey: '',
          smsPhoneNumber: '',
          whatsappEnabled: false,
          whatsappApiKey: '',
          whatsappPhoneNumberId: '',
          whatsappRecipientNumber: '',
          
          // Auto sync settings
          autoSyncEnabled: true,
          syncFrequencyMinutes: 15,
          
          // Animation settings
          autoDismissEnabled: true,
          autoDismissDuration: '3000',
          confettiEnabled: false,
          confettiSpeed: '1.0',
          
          // Printer settings
          printerEnabled: false,
          printerConnectionType: 'Network',
          printerIp: '',
          printerPort: '9100',
          printerPaperWidth: '58mm',
          printerCharacterSet: 'PC437',
          printerDpi: 203,
          printerCutPaper: true,
          printerBoldHeader: true,
          printerLogoEnabled: false,
          printerHeaderText: selectedHierarchy.outlet.name || '',
          printerFooterText: 'Thank you for your business!',
          printerAutoPrintReceipt: true,
          lastBluetoothDeviceName: '',
          lastBluetoothDeviceAddress: '',
          
          // KOT Printer settings
          kotPrinterEnabled: false,
          kotPrinterConnectionType: 'Network',
          kotPrinterIp: '',
          kotPrinterPort: '9100',
          kotPrinterPaperWidth: '58mm',
          kotPrinterCharacterSet: 'PC437',
          kotPrinterDpi: 203,
          kotPrinterCutPaper: true,
          kotPrinterBoldHeader: true,
          kotPrinterLogoEnabled: false,
          kotPrinterHeaderText: 'KITCHEN ORDER TICKET',
          kotPrinterFooterText: 'Kitchen - Prepare Order',
          kotAutoPrintKOT: false,
          lastKOTBluetoothDeviceName: '',
          lastKOTBluetoothDeviceAddress: '',
          
          // Currency settings
          currencyCode: 'INR',
          currencySymbol: '₹',
          
          // UPI settings
          upiIds: [],
          upiMerchantName: '',
          
          // Payment settings
          splitPaymentEnabled: true,
          expressCheckoutEnabled: false,
          employeeAuthValidationOnCheckoutEnabled: false,
          
          // POS Screen settings
          onDemandTabEnabled: false,
          
          // Discount settings
          discountPercentageStepFactor: 0.05,
          discountFixedAmountStepFactor: 5.0,
          discountItemsPerPage: 25,
          
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        alert('Terminal created successfully');
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving terminal:', error);
      alert('Error saving terminal: ' + error.message);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingTerminal) return;

    try {
      const settings = terminalSettings[editingTerminal.id];
      
      const settingsData = {
        ...settingsFormData,
        terminalId: editingTerminal.id,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };

      if (settings) {
        // Update existing settings
        await updateDoc(doc(db, 'terminal_settings', settings.id), settingsData);
      } else {
        // Create new settings
        settingsData.createdAt = new Date();
        await addDoc(collection(db, 'terminal_settings'), settingsData);
      }

      alert('Terminal settings updated successfully');
      setShowSettingsModal(false);
      setEditingTerminal(null);
      loadTerminals();
    } catch (error) {
      console.error('Error saving terminal settings:', error);
      alert('Error saving terminal settings: ' + error.message);
    }
  };

  const handleEdit = (terminal) => {
    setEditingTerminal(terminal);
    setFormData({
      name: terminal.name,
      deviceId: terminal.deviceId,
      isActive: terminal.isActive
    });
    setShowModal(true);
  };

  const handleEditSettings = (terminal) => {
    setEditingTerminal(terminal);
    const settings = terminalSettings[terminal.id];
    
    if (settings) {
      setSettingsFormData({
        // Theme settings
        themeMode: settings.themeMode || 'light',
        showStockInfo: settings.showStockInfo !== undefined ? settings.showStockInfo : true,
        categoryFontSize: settings.categoryFontSize || 14,
        productFontSize: settings.productFontSize || 15,
        primaryColor: settings.primaryColor || 0xFF2196F3,
        
        // Security settings
        pinAuthEnabled: settings.pinAuthEnabled || false,
        pinCode: settings.pinCode || '',
        faceAuthEnabled: settings.faceAuthEnabled || false,
        faceAuthFastMode: settings.faceAuthFastMode || false,
        faceAuthThreshold: settings.faceAuthThreshold || 0.75,
        
        // Weather settings
        weatherEnabled: settings.weatherEnabled || false,
        weatherApiKey: settings.weatherApiKey || '',
        useCurrentLocation: settings.useCurrentLocation !== undefined ? settings.useCurrentLocation : true,
        manualLatitude: settings.manualLatitude || '',
        manualLongitude: settings.manualLongitude || '',
        weatherForceRefresh: settings.weatherForceRefresh || false,
        weatherApiProvider: settings.weatherApiProvider || 'openmeteo',
        weatherCacheDurationMinutes: settings.weatherCacheDurationMinutes || 60,
        
        // SMS & WhatsApp settings
        smsEnabled: settings.smsEnabled || false,
        smsApiKey: settings.smsApiKey || '',
        smsPhoneNumber: settings.smsPhoneNumber || '',
        whatsappEnabled: settings.whatsappEnabled || false,
        whatsappApiKey: settings.whatsappApiKey || '',
        whatsappPhoneNumberId: settings.whatsappPhoneNumberId || '',
        whatsappRecipientNumber: settings.whatsappRecipientNumber || '',
        
        // Auto sync settings
        autoSyncEnabled: settings.autoSyncEnabled !== undefined ? settings.autoSyncEnabled : true,
        syncFrequencyMinutes: settings.syncFrequencyMinutes || 15,
        
        // Animation settings
        autoDismissEnabled: settings.autoDismissEnabled !== undefined ? settings.autoDismissEnabled : true,
        autoDismissDuration: settings.autoDismissDuration || '3000',
        confettiEnabled: settings.confettiEnabled || false,
        confettiSpeed: settings.confettiSpeed || '1.0',
        
        // Printer settings
        printerEnabled: settings.printerEnabled || false,
        printerConnectionType: settings.printerConnectionType || 'Network',
        printerIp: settings.printerIp || '',
        printerPort: settings.printerPort || '9100',
        printerPaperWidth: settings.printerPaperWidth || '58mm',
        printerCharacterSet: settings.printerCharacterSet || 'PC437',
        printerDpi: settings.printerDpi || 203,
        printerCutPaper: settings.printerCutPaper !== undefined ? settings.printerCutPaper : true,
        printerBoldHeader: settings.printerBoldHeader !== undefined ? settings.printerBoldHeader : true,
        printerLogoEnabled: settings.printerLogoEnabled || false,
        printerHeaderText: settings.printerHeaderText || '',
        printerFooterText: settings.printerFooterText || '',
        printerAutoPrintReceipt: settings.printerAutoPrintReceipt !== undefined ? settings.printerAutoPrintReceipt : true,
        lastBluetoothDeviceName: settings.lastBluetoothDeviceName || '',
        lastBluetoothDeviceAddress: settings.lastBluetoothDeviceAddress || '',
        
        // KOT Printer settings
        kotPrinterEnabled: settings.kotPrinterEnabled || false,
        kotPrinterConnectionType: settings.kotPrinterConnectionType || 'Network',
        kotPrinterIp: settings.kotPrinterIp || '',
        kotPrinterPort: settings.kotPrinterPort || '9100',
        kotPrinterPaperWidth: settings.kotPrinterPaperWidth || '58mm',
        kotPrinterCharacterSet: settings.kotPrinterCharacterSet || 'PC437',
        kotPrinterDpi: settings.kotPrinterDpi || 203,
        kotPrinterCutPaper: settings.kotPrinterCutPaper !== undefined ? settings.kotPrinterCutPaper : true,
        kotPrinterBoldHeader: settings.kotPrinterBoldHeader !== undefined ? settings.kotPrinterBoldHeader : true,
        kotPrinterLogoEnabled: settings.kotPrinterLogoEnabled || false,
        kotPrinterHeaderText: settings.kotPrinterHeaderText || 'KITCHEN ORDER TICKET',
        kotPrinterFooterText: settings.kotPrinterFooterText || 'Kitchen - Prepare Order',
        kotAutoPrintKOT: settings.kotAutoPrintKOT || false,
        lastKOTBluetoothDeviceName: settings.lastKOTBluetoothDeviceName || '',
        lastKOTBluetoothDeviceAddress: settings.lastKOTBluetoothDeviceAddress || '',
        
        // Currency settings
        currencyCode: settings.currencyCode || 'INR',
        currencySymbol: settings.currencySymbol || '₹',
        
        // UPI settings
        upiIds: settings.upiIds || [],
        upiMerchantName: settings.upiMerchantName || '',
        
        // Payment settings
        splitPaymentEnabled: settings.splitPaymentEnabled !== undefined ? settings.splitPaymentEnabled : true,
        expressCheckoutEnabled: settings.expressCheckoutEnabled || false,
        employeeAuthValidationOnCheckoutEnabled: settings.employeeAuthValidationOnCheckoutEnabled || false,
        
        // POS Screen settings
        onDemandTabEnabled: settings.onDemandTabEnabled || false,
        
        // Discount settings
        discountPercentageStepFactor: settings.discountPercentageStepFactor || 0.05,
        discountFixedAmountStepFactor: settings.discountFixedAmountStepFactor || 5.0,
        discountItemsPerPage: settings.discountItemsPerPage || 25
      });
    } else {
      setSettingsFormData({
        // Theme settings
        themeMode: 'light',
        showStockInfo: true,
        categoryFontSize: 14,
        productFontSize: 15,
        primaryColor: 0xFF2196F3,
        
        // Security settings
        pinAuthEnabled: false,
        pinCode: '',
        faceAuthEnabled: false,
        faceAuthFastMode: false,
        faceAuthThreshold: 0.75,
        
        // Weather settings
        weatherEnabled: false,
        weatherApiKey: '',
        useCurrentLocation: true,
        manualLatitude: '',
        manualLongitude: '',
        weatherForceRefresh: false,
        weatherApiProvider: 'openmeteo',
        weatherCacheDurationMinutes: 60,
        
        // SMS & WhatsApp settings
        smsEnabled: false,
        smsApiKey: '',
        smsPhoneNumber: '',
        whatsappEnabled: false,
        whatsappApiKey: '',
        whatsappPhoneNumberId: '',
        whatsappRecipientNumber: '',
        
        // Auto sync settings
        autoSyncEnabled: true,
        syncFrequencyMinutes: 15,
        
        // Animation settings
        autoDismissEnabled: true,
        autoDismissDuration: '3000',
        confettiEnabled: false,
        confettiSpeed: '1.0',
        
        // Printer settings
        printerEnabled: false,
        printerConnectionType: 'Network',
        printerIp: '',
        printerPort: '9100',
        printerPaperWidth: '58mm',
        printerCharacterSet: 'PC437',
        printerDpi: 203,
        printerCutPaper: true,
        printerBoldHeader: true,
        printerLogoEnabled: false,
        printerHeaderText: selectedHierarchy.outlet.name || '',
        printerFooterText: 'Thank you for your business!',
        printerAutoPrintReceipt: true,
        lastBluetoothDeviceName: '',
        lastBluetoothDeviceAddress: '',
        
        // KOT Printer settings
        kotPrinterEnabled: false,
        kotPrinterConnectionType: 'Network',
        kotPrinterIp: '',
        kotPrinterPort: '9100',
        kotPrinterPaperWidth: '58mm',
        kotPrinterCharacterSet: 'PC437',
        kotPrinterDpi: 203,
        kotPrinterCutPaper: true,
        kotPrinterBoldHeader: true,
        kotPrinterLogoEnabled: false,
        kotPrinterHeaderText: 'KITCHEN ORDER TICKET',
        kotPrinterFooterText: 'Kitchen - Prepare Order',
        kotAutoPrintKOT: false,
        lastKOTBluetoothDeviceName: '',
        lastKOTBluetoothDeviceAddress: '',
        
        // Currency settings
        currencyCode: 'INR',
        currencySymbol: '₹',
        
        // UPI settings
        upiIds: [],
        upiMerchantName: '',
        
        // Payment settings
        splitPaymentEnabled: true,
        expressCheckoutEnabled: false,
        employeeAuthValidationOnCheckoutEnabled: false,
        
        // POS Screen settings
        onDemandTabEnabled: false,
        
        // Discount settings
        discountPercentageStepFactor: 0.05,
        discountFixedAmountStepFactor: 5.0,
        discountItemsPerPage: 25
      });
    }
    
    setShowSettingsModal(true);
  };

  const handleDelete = async (terminalId) => {
    if (!window.confirm('Are you sure you want to delete this terminal?')) return;

    try {
      await deleteDoc(doc(db, 'terminals', terminalId));
      
      // Also delete settings if they exist
      const settings = terminalSettings[terminalId];
      if (settings) {
        await deleteDoc(doc(db, 'terminal_settings', settings.id));
      }
      
      alert('Terminal deleted successfully');
    } catch (error) {
      console.error('Error deleting terminal:', error);
      alert('Error deleting terminal: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      deviceId: '',
      isActive: true
    });
    setEditingTerminal(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleAddUpiId = () => {
    setSettingsFormData(prev => ({
      ...prev,
      upiIds: [...prev.upiIds, '']
    }));
  };

  const handleRemoveUpiId = (index) => {
    setSettingsFormData(prev => ({
      ...prev,
      upiIds: prev.upiIds.filter((_, i) => i !== index)
    }));
  };

  const handleUpiIdChange = (index, value) => {
    setSettingsFormData(prev => ({
      ...prev,
      upiIds: prev.upiIds.map((id, i) => i === index ? value : id)
    }));
  };

  // Filter terminals
  const filteredTerminals = terminals.filter(terminal => {
    const matchesSearch = terminal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         terminal.deviceId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || 
                         (statusFilter === 'active' && terminal.isActive) ||
                         (statusFilter === 'inactive' && !terminal.isActive);
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Terminals Management</h2>
        <button className="btn btn-primary" onClick={handleAddNew}>
          <FiPlus className="me-2" />
          Add Terminal
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Total Terminals</h6>
                  <h2 className="card-title mb-0">
                    <AnimatedCounter value={stats.totalTerminals} />
                  </h2>
                </div>
                <FiMonitor size={48} className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-success text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Active Terminals</h6>
                  <h2 className="card-title mb-0">
                    <AnimatedCounter value={stats.activeTerminals} />
                  </h2>
                </div>
                <FiCheckCircle size={48} className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-secondary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">Inactive Terminals</h6>
                  <h2 className="card-title mb-0">
                    <AnimatedCounter value={stats.inactiveTerminals} />
                  </h2>
                </div>
                <FiXCircle size={48} className="opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or device ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Terminals Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Device ID</th>
                  <th>Status</th>
                  <th>Has Settings</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTerminals.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      No terminals found
                    </td>
                  </tr>
                ) : (
                  filteredTerminals.map((terminal) => (
                    <tr key={terminal.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FiMonitor className="me-2 text-primary" />
                          <strong>{terminal.name}</strong>
                        </div>
                      </td>
                      <td>
                        <code className="text-muted">{terminal.deviceId}</code>
                      </td>
                      <td>
                        {terminal.isActive ? (
                          <span className="badge bg-success">
                            <FiCheckCircle className="me-1" />
                            Active
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <FiXCircle className="me-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td>
                        {terminalSettings[terminal.id] ? (
                          <span className="badge bg-info">
                            <FiSettings className="me-1" />
                            Configured
                          </span>
                        ) : (
                          <span className="badge bg-warning">
                            Not Configured
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(terminal)}
                            title="Edit Terminal"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn btn-outline-info"
                            onClick={() => handleEditSettings(terminal)}
                            title="Configure Settings"
                          >
                            <FiSettings />
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(terminal.id)}
                            title="Delete Terminal"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Terminal Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingTerminal ? 'Edit Terminal' : 'Add New Terminal'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Terminal Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Device ID *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.deviceId}
                      onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
                      required
                    />
                    <small className="form-text text-muted">
                      Unique identifier for this terminal device
                    </small>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingTerminal ? 'Update' : 'Create'} Terminal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Settings Modal */}
      {showSettingsModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: '90%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Terminal Settings - {editingTerminal?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSettingsModal(false);
                    setEditingTerminal(null);
                    setSettingsActiveTab('theme');
                  }}
                />
              </div>
              <form onSubmit={handleSettingsSubmit}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {/* Tabs Navigation */}
                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'appearance' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('appearance')}
                      >
                        Appearance
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'weather' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('weather')}
                      >
                        Weather
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'animation' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('animation')}
                      >
                        Animation
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'security' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('security')}
                      >
                        Security
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'printers' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('printers')}
                      >
                        <FiPrinter className="me-1" />
                        Printers
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'currency' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('currency')}
                      >
                        Currency
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'payment' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('payment')}
                      >
                        Payment
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'pos' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('pos')}
                      >
                        POS Screen
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'sms' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('sms')}
                      >
                        SMS
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'whatsapp' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('whatsapp')}
                      >
                        WhatsApp
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${settingsActiveTab === 'sync' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setSettingsActiveTab('sync')}
                      >
                        Sync
                      </button>
                    </li>
                  </ul>

                  {/* Appearance Tab */}
                  {settingsActiveTab === 'appearance' && (
                    <div>
                      <h6 className="mb-3">Theme Settings</h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Theme Mode</label>
                          <select
                            className="form-select"
                            value={settingsFormData.themeMode}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, themeMode: e.target.value })}
                          >
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                            <option value="system">System</option>
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Primary Color</label>
                          <select
                            className="form-select"
                            value={settingsFormData.primaryColor}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, primaryColor: parseInt(e.target.value) })}
                          >
                            {metroColors.map((colorData) => (
                              <option 
                                key={colorData.color} 
                                value={colorData.color}
                                style={{ 
                                  color: colorData.hex,
                                  fontWeight: 'bold'
                                }}
                              >
                                ● {colorData.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Category Font Size</label>
                          <input
                            type="number"
                            step="1"
                            min="10"
                            max="24"
                            className="form-control"
                            value={settingsFormData.categoryFontSize}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, categoryFontSize: parseFloat(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Product Font Size</label>
                          <input
                            type="number"
                            step="1"
                            min="10"
                            max="24"
                            className="form-control"
                            value={settingsFormData.productFontSize}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, productFontSize: parseFloat(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="form-check mt-4">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="showStockInfo"
                              checked={settingsFormData.showStockInfo}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, showStockInfo: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="showStockInfo">
                              Show Stock Info
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Weather Tab */}
                  {settingsActiveTab === 'weather' && (
                    <div>
                      <h6 className="mb-3">Weather Settings</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="weatherEnabled"
                          checked={settingsFormData.weatherEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, weatherEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="weatherEnabled">
                          Enable Weather Display
                        </label>
                      </div>
                      
                      {settingsFormData.weatherEnabled && (
                        <>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Weather API Provider</label>
                              <select
                                className="form-select"
                                value={settingsFormData.weatherApiProvider}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, weatherApiProvider: e.target.value })}
                              >
                                <option value="openmeteo">Open-Meteo (Free)</option>
                                <option value="openweathermap">OpenWeatherMap</option>
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">API Key (if required)</label>
                              <input
                                type="text"
                                className="form-control"
                                value={settingsFormData.weatherApiKey}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, weatherApiKey: e.target.value })}
                                placeholder="API Key"
                              />
                            </div>
                          </div>
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="useCurrentLocation"
                              checked={settingsFormData.useCurrentLocation}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, useCurrentLocation: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="useCurrentLocation">
                              Use Current Location (GPS)
                            </label>
                          </div>
                          {!settingsFormData.useCurrentLocation && (
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label">Latitude</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.manualLatitude}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, manualLatitude: e.target.value })}
                                  placeholder="28.6139"
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label">Longitude</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.manualLongitude}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, manualLongitude: e.target.value })}
                                  placeholder="77.2090"
                                />
                              </div>
                            </div>
                          )}
                          <div className="mb-3">
                            <label className="form-label">Cache Duration (minutes)</label>
                            <input
                              type="number"
                              min="1"
                              className="form-control"
                              value={settingsFormData.weatherCacheDurationMinutes}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, weatherCacheDurationMinutes: parseInt(e.target.value) })}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Animation Tab */}
                  {settingsActiveTab === 'animation' && (
                    <div>
                      <h6 className="mb-3">Animation Settings</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="autoDismissEnabled"
                          checked={settingsFormData.autoDismissEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, autoDismissEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="autoDismissEnabled">
                          Auto Dismiss Notifications
                        </label>
                      </div>
                      {settingsFormData.autoDismissEnabled && (
                        <div className="mb-3">
                          <label className="form-label">Auto Dismiss Duration (ms)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settingsFormData.autoDismissDuration}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, autoDismissDuration: e.target.value })}
                            placeholder="3000"
                          />
                        </div>
                      )}
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="confettiEnabled"
                          checked={settingsFormData.confettiEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, confettiEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="confettiEnabled">
                          Enable Confetti Animation
                        </label>
                      </div>
                      {settingsFormData.confettiEnabled && (
                        <div className="mb-3">
                          <label className="form-label">Confetti Speed</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settingsFormData.confettiSpeed}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, confettiSpeed: e.target.value })}
                            placeholder="1.0"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Security Tab */}
                  {settingsActiveTab === 'security' && (
                    <div>
                      <h6 className="mb-3">PIN Authentication</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="pinAuthEnabled"
                          checked={settingsFormData.pinAuthEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, pinAuthEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="pinAuthEnabled">
                          Enable PIN Authentication
                        </label>
                      </div>
                      {settingsFormData.pinAuthEnabled && (
                        <div className="mb-3">
                          <label className="form-label">PIN Code</label>
                          <input
                            type="password"
                            className="form-control"
                            maxLength="6"
                            value={settingsFormData.pinCode}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, pinCode: e.target.value })}
                            placeholder="Enter 4-6 digit PIN"
                          />
                        </div>
                      )}
                      
                      <hr />
                      <h6 className="mb-3">Face Authentication</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="faceAuthEnabled"
                          checked={settingsFormData.faceAuthEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, faceAuthEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="faceAuthEnabled">
                          Enable Face Authentication
                        </label>
                      </div>
                      {settingsFormData.faceAuthEnabled && (
                        <>
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="faceAuthFastMode"
                              checked={settingsFormData.faceAuthFastMode}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, faceAuthFastMode: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="faceAuthFastMode">
                              Fast Mode (Less accurate, faster)
                            </label>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Recognition Threshold (0.0 - 1.0)</label>
                            <input
                              type="number"
                              step="0.05"
                              min="0"
                              max="1"
                              className="form-control"
                              value={settingsFormData.faceAuthThreshold}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, faceAuthThreshold: parseFloat(e.target.value) })}
                            />
                            <small className="text-muted">Higher values require closer matches</small>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Printers Tab */}
                  {settingsActiveTab === 'printers' && (
                    <div>
                      <h6 className="mb-4">Receipt Printer</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="printerEnabled"
                          checked={settingsFormData.printerEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, printerEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="printerEnabled">
                          <strong>Enable Receipt Printer</strong>
                        </label>
                      </div>
                      
                      {settingsFormData.printerEnabled && (
                        <>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Connection Type</label>
                              <select
                                className="form-select"
                                value={settingsFormData.printerConnectionType}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, printerConnectionType: e.target.value })}
                              >
                                <option value="Network">Network</option>
                                <option value="Bluetooth">Bluetooth</option>
                                <option value="USB">USB</option>
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Paper Width</label>
                              <select
                                className="form-select"
                                value={settingsFormData.printerPaperWidth}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, printerPaperWidth: e.target.value })}
                              >
                                <option value="58mm">58mm</option>
                                <option value="80mm">80mm</option>
                              </select>
                            </div>
                          </div>

                          {settingsFormData.printerConnectionType === 'Network' && (
                            <div className="row">
                              <div className="col-md-8 mb-3">
                                <label className="form-label">Printer IP Address</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.printerIp}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, printerIp: e.target.value })}
                                  placeholder="192.168.1.100"
                                />
                              </div>
                              <div className="col-md-4 mb-3">
                                <label className="form-label">Port</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.printerPort}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, printerPort: e.target.value })}
                                  placeholder="9100"
                                />
                              </div>
                            </div>
                          )}

                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Character Set</label>
                              <select
                                className="form-select"
                                value={settingsFormData.printerCharacterSet}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, printerCharacterSet: e.target.value })}
                              >
                                <option value="PC437">PC437 (USA)</option>
                                <option value="PC850">PC850 (Multilingual)</option>
                                <option value="PC858">PC858 (Euro)</option>
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">DPI</label>
                              <select
                                className="form-select"
                                value={settingsFormData.printerDpi}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, printerDpi: parseInt(e.target.value) })}
                              >
                                <option value="203">203 DPI</option>
                                <option value="300">300 DPI</option>
                              </select>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Receipt Header Text</label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={settingsFormData.printerHeaderText}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, printerHeaderText: e.target.value })}
                              placeholder="Store Name, Address, etc."
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Receipt Footer Text</label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={settingsFormData.printerFooterText}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, printerFooterText: e.target.value })}
                              placeholder="Thank you message"
                            />
                          </div>

                          <div className="row">
                            <div className="col-md-4">
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="printerCutPaper"
                                  checked={settingsFormData.printerCutPaper}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, printerCutPaper: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="printerCutPaper">
                                  Auto Cut Paper
                                </label>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="printerBoldHeader"
                                  checked={settingsFormData.printerBoldHeader}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, printerBoldHeader: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="printerBoldHeader">
                                  Bold Header
                                </label>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="printerAutoPrintReceipt"
                                  checked={settingsFormData.printerAutoPrintReceipt}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, printerAutoPrintReceipt: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="printerAutoPrintReceipt">
                                  Auto Print Receipt
                                </label>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <hr className="my-4" />
                      
                      <h6 className="mb-4">KOT (Kitchen Order Ticket) Printer</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="kotPrinterEnabled"
                          checked={settingsFormData.kotPrinterEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="kotPrinterEnabled">
                          <strong>Enable KOT Printer</strong>
                        </label>
                      </div>
                      
                      {settingsFormData.kotPrinterEnabled && (
                        <>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Connection Type</label>
                              <select
                                className="form-select"
                                value={settingsFormData.kotPrinterConnectionType}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterConnectionType: e.target.value })}
                              >
                                <option value="Network">Network</option>
                                <option value="Bluetooth">Bluetooth</option>
                                <option value="USB">USB</option>
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Paper Width</label>
                              <select
                                className="form-select"
                                value={settingsFormData.kotPrinterPaperWidth}
                                onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterPaperWidth: e.target.value })}
                              >
                                <option value="58mm">58mm</option>
                                <option value="80mm">80mm</option>
                              </select>
                            </div>
                          </div>

                          {settingsFormData.kotPrinterConnectionType === 'Network' && (
                            <div className="row">
                              <div className="col-md-8 mb-3">
                                <label className="form-label">Printer IP Address</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.kotPrinterIp}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterIp: e.target.value })}
                                  placeholder="192.168.1.101"
                                />
                              </div>
                              <div className="col-md-4 mb-3">
                                <label className="form-label">Port</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={settingsFormData.kotPrinterPort}
                                  onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterPort: e.target.value })}
                                  placeholder="9100"
                                />
                              </div>
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="form-label">KOT Header Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.kotPrinterHeaderText}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterHeaderText: e.target.value })}
                              placeholder="KITCHEN ORDER TICKET"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">KOT Footer Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.kotPrinterFooterText}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, kotPrinterFooterText: e.target.value })}
                              placeholder="Kitchen - Prepare Order"
                            />
                          </div>

                          <div className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="kotAutoPrintKOT"
                              checked={settingsFormData.kotAutoPrintKOT}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, kotAutoPrintKOT: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="kotAutoPrintKOT">
                              Auto Print KOT
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Payment Tab */}
                  {settingsActiveTab === 'payment' && (
                    <div>
                      <h6 className="mb-3">Payment Options</h6>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="splitPaymentEnabled"
                              checked={settingsFormData.splitPaymentEnabled}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, splitPaymentEnabled: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="splitPaymentEnabled">
                              Enable Split Payment
                            </label>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="expressCheckoutEnabled"
                              checked={settingsFormData.expressCheckoutEnabled}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, expressCheckoutEnabled: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="expressCheckoutEnabled">
                              Enable Express Checkout
                            </label>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="employeeAuthValidationOnCheckoutEnabled"
                              checked={settingsFormData.employeeAuthValidationOnCheckoutEnabled}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, employeeAuthValidationOnCheckoutEnabled: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="employeeAuthValidationOnCheckoutEnabled">
                              Employee Auth on Checkout
                            </label>
                          </div>
                        </div>
                      </div>

                      <hr />
                      <h6 className="mb-3">UPI Settings</h6>
                      <div className="mb-3">
                        <label className="form-label">UPI Merchant Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={settingsFormData.upiMerchantName}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, upiMerchantName: e.target.value })}
                          placeholder="Your Store Name"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">UPI IDs</label>
                        {settingsFormData.upiIds.map((upiId, index) => (
                          <div key={index} className="input-group mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="merchant@upi"
                              value={upiId}
                              onChange={(e) => handleUpiIdChange(index, e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => handleRemoveUpiId(index)}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleAddUpiId}
                        >
                          <FiPlus className="me-1" />
                          Add UPI ID
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Currency Tab */}
                  {settingsActiveTab === 'currency' && (
                    <div>
                      <h6 className="mb-3">Currency Settings</h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Currency Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settingsFormData.currencyCode}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, currencyCode: e.target.value })}
                            placeholder="INR"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Currency Symbol</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settingsFormData.currencySymbol}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, currencySymbol: e.target.value })}
                            placeholder="₹"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* POS Screen Tab */}
                  {settingsActiveTab === 'pos' && (
                    <div>
                      <h6 className="mb-3">POS Screen Settings</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="onDemandTabEnabled"
                          checked={settingsFormData.onDemandTabEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, onDemandTabEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="onDemandTabEnabled">
                          Enable On-Demand Tab
                        </label>
                      </div>

                      <hr />
                      <h6 className="mb-3">Discount Settings</h6>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Percentage Step Factor</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="form-control"
                            value={settingsFormData.discountPercentageStepFactor}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, discountPercentageStepFactor: parseFloat(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Fixed Amount Step Factor</label>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            className="form-control"
                            value={settingsFormData.discountFixedAmountStepFactor}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, discountFixedAmountStepFactor: parseFloat(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Items Per Page</label>
                          <input
                            type="number"
                            min="5"
                            max="50"
                            className="form-control"
                            value={settingsFormData.discountItemsPerPage}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, discountItemsPerPage: parseInt(e.target.value) })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SMS Tab */}
                  {settingsActiveTab === 'sms' && (
                    <div>
                      <h6 className="mb-3">SMS Integration</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="smsEnabled"
                          checked={settingsFormData.smsEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, smsEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="smsEnabled">
                          Enable SMS Notifications
                        </label>
                      </div>
                      {settingsFormData.smsEnabled && (
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">SMS API Key</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.smsApiKey}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, smsApiKey: e.target.value })}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">SMS Phone Number</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.smsPhoneNumber}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, smsPhoneNumber: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* WhatsApp Tab */}
                  {settingsActiveTab === 'whatsapp' && (
                    <div>
                      <h6 className="mb-3">WhatsApp Integration</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="whatsappEnabled"
                          checked={settingsFormData.whatsappEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, whatsappEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="whatsappEnabled">
                          Enable WhatsApp Notifications
                        </label>
                      </div>
                      {settingsFormData.whatsappEnabled && (
                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label className="form-label">WhatsApp API Key</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.whatsappApiKey}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, whatsappApiKey: e.target.value })}
                            />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Phone Number ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.whatsappPhoneNumberId}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, whatsappPhoneNumberId: e.target.value })}
                            />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Recipient Number</label>
                            <input
                              type="text"
                              className="form-control"
                              value={settingsFormData.whatsappRecipientNumber}
                              onChange={(e) => setSettingsFormData({ ...settingsFormData, whatsappRecipientNumber: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sync Tab */}
                  {settingsActiveTab === 'sync' && (
                    <div>
                      <h6 className="mb-3">Auto Sync Settings</h6>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="autoSyncEnabled"
                          checked={settingsFormData.autoSyncEnabled}
                          onChange={(e) => setSettingsFormData({ ...settingsFormData, autoSyncEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="autoSyncEnabled">
                          Enable Auto Sync
                        </label>
                      </div>
                      {settingsFormData.autoSyncEnabled && (
                        <div className="mb-3">
                          <label className="form-label">Sync Frequency (minutes)</label>
                          <input
                            type="number"
                            min="5"
                            className="form-control"
                            value={settingsFormData.syncFrequencyMinutes}
                            onChange={(e) => setSettingsFormData({ ...settingsFormData, syncFrequencyMinutes: parseInt(e.target.value) })}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowSettingsModal(false);
                      setEditingTerminal(null);
                      setSettingsActiveTab('theme');
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Settings
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
